import { useState, useEffect } from "react";
import { Loader2, MapPin, Phone, Building2, Save, Check, AlertCircle, Trophy, Telescope, Sparkles } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";

export default function LocalSeoPage() {
  const { active } = useProjects();
  return (
    <DashboardLayout
      eyebrow="Local SEO"
      title="Win the local pack"
      subtitle="Track local rankings, audit NAP consistency, and generate geo-targeted keywords."
    >
      {!active ? (
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-8 text-center" data-testid="local-no-project">
          <Building2 className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">Select or create a project to use Local SEO tools.</p>
        </div>
      ) : (
        <Tabs defaultValue="profile">
          <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="local-tabs">
            <TabsTrigger value="profile" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-profile">
              <Building2 className="w-3.5 h-3.5 mr-1.5" /> Business profile
            </TabsTrigger>
            <TabsTrigger value="nap" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-nap">
              <Phone className="w-3.5 h-3.5 mr-1.5" /> NAP audit
            </TabsTrigger>
            <TabsTrigger value="rank" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-rank">
              <Trophy className="w-3.5 h-3.5 mr-1.5" /> Local rank
            </TabsTrigger>
            <TabsTrigger value="keywords" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-keywords">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Geo keywords
            </TabsTrigger>
          </TabsList>
          <TabsContent value="profile" className="mt-5"><ProfileTab projectId={active.id} domain={active.domain} /></TabsContent>
          <TabsContent value="nap" className="mt-5"><NapAuditTab projectId={active.id} /></TabsContent>
          <TabsContent value="rank" className="mt-5"><RankTab defaultDomain={active.domain} /></TabsContent>
          <TabsContent value="keywords" className="mt-5"><GeoKeywordsTab /></TabsContent>
        </Tabs>
      )}
    </DashboardLayout>
  );
}

function ProfileTab({ projectId, domain }) {
  const [form, setForm] = useState({ name: "", phone: "", address: "", city: "", country: "United States", gbp_url: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get(`/local-seo/business/${projectId}`).then(({ data }) => {
      if (cancelled) return;
      setForm((f) => ({ ...f, ...data }));
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [projectId]);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr(""); setSaved(false);
    try {
      await api.post(`/local-seo/business/${projectId}`, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-zinc-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  return (
    <form onSubmit={submit} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 max-w-2xl space-y-4" data-testid="local-profile-form">
      <p className="text-xs text-zinc-500 leading-relaxed">
        Save your business <strong>NAP</strong> (Name, Address, Phone) so RankPilot can audit consistency across your site and citation pages.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Business name</Label>
          <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="profile-name" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Phone</Label>
          <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} required
                 placeholder="+1 555 555 1234"
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="profile-phone" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">City</Label>
          <Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })}
                 placeholder="Austin"
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="profile-city" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Full address</Label>
          <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} required
                 placeholder="123 Main St, Austin, TX 78701"
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="profile-address" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Country</Label>
          <Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })}
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="profile-country" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Google Business URL</Label>
          <Input value={form.gbp_url || ""} onChange={(e) => setForm({ ...form, gbp_url: e.target.value })}
                 placeholder="https://maps.google.com/?cid=..."
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="profile-gbp" />
        </div>
      </div>
      {err && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="profile-error">{err}</div>}
      <div className="flex items-center gap-3 pt-2">
        <Button type="submit" disabled={saving} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="profile-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save profile</>}
        </Button>
        {saved && <span className="text-sm text-[#10B981] flex items-center gap-1"><Check className="w-4 h-4" /> Saved</span>}
        <span className="text-xs text-zinc-600 ml-auto">Site: <code className="text-zinc-500">{domain}</code></span>
      </div>
    </form>
  );
}

function NapAuditTab({ projectId }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const run = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/local-seo/nap-audit", { project_id: projectId, url });
      setResult(data);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={run} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end" data-testid="nap-form">
        <div className="flex-1">
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">URL to audit</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} required placeholder="https://yelp.com/biz/your-place"
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="nap-url" />
        </div>
        <Button type="submit" disabled={busy} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="nap-run">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run NAP audit"}
        </Button>
      </form>
      {err && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="nap-error">{err}</div>}
      {result && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 space-y-4" data-testid="nap-result">
          <div className="flex items-center gap-4">
            <div className={`w-20 h-20 rounded-sm grid place-items-center font-display text-3xl font-semibold ${result.score >= 80 ? "bg-[#10B981]/15 text-[#10B981]" : result.score >= 50 ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400"}`}>
              {result.score}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">Consistency score</div>
              <div className="text-zinc-300 text-sm mt-1">{result.score >= 80 ? "Strong NAP — citations match your profile." : result.score >= 50 ? "Partial match — fix the items below to boost local rankings." : "Major inconsistency — Google may not trust this listing."}</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Pill label="Name on page" ok={result.name_present} />
            <Pill label="Phone matches" ok={result.phone_match} />
            <Pill label="Address matches" ok={result.address_match} />
          </div>
          {!!result.issues?.length && (
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Issues</div>
              <ul className="space-y-1.5">
                {result.issues.map((i, idx) => (
                  <li key={idx} className="text-sm text-red-300 flex items-start gap-2"><AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {i}</li>
                ))}
              </ul>
            </div>
          )}
          {!!result.phones_found?.length && (
            <div className="text-xs text-zinc-500">Phones found on page: <span className="text-zinc-300">{result.phones_found.join(", ")}</span></div>
          )}
        </div>
      )}
    </div>
  );
}

function Pill({ label, ok }) {
  return (
    <div className={`px-3 py-2 rounded-sm border text-sm flex items-center gap-2 ${ok ? "bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]" : "bg-red-950/30 border-red-900/40 text-red-300"}`}>
      {ok ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />} {label}
    </div>
  );
}

function RankTab({ defaultDomain }) {
  const [form, setForm] = useState({ keyword: "", location: "Austin, Texas, United States", domain: defaultDomain || "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/local-seo/recent").then(({ data }) => setRecent(data.items || [])).catch(() => {});
  }, [result]);

  const run = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/local-seo/rank-check", form);
      setResult(data);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={run} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 grid sm:grid-cols-3 gap-3 items-end" data-testid="rank-form">
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Keyword</Label>
          <Input value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} required
                 placeholder="best dentist"
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="rank-keyword" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Location</Label>
          <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="rank-location" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Domain</Label>
          <Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} required
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="rank-domain" />
        </div>
        <div className="sm:col-span-3">
          <Button type="submit" disabled={busy} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="rank-run">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Telescope className="w-4 h-4 mr-2" /> Check rank</>}
          </Button>
        </div>
      </form>
      {err && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="rank-error">{err}</div>}
      {result && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 space-y-5" data-testid="rank-result">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Organic position</div>
              <div className="font-display text-3xl">{result.organic_position ?? <span className="text-zinc-600">Not in top 20</span>}</div>
              {result.organic_url && <div className="text-xs text-zinc-500 mt-1 truncate">{result.organic_url}</div>}
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Local Pack</div>
              <div className="font-display text-3xl">
                {result.in_local_pack ? <span className="text-[#10B981]">#{result.local_pack_position}</span> : <span className="text-zinc-600">Not ranking</span>}
              </div>
            </div>
          </div>
          {!!result.top_competitors?.length && (
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Top 3 Local Pack competitors</div>
              <div className="space-y-2">
                {result.top_competitors.map((c, i) => (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 flex flex-wrap items-center gap-3" data-testid={`competitor-${i}`}>
                    <Badge className="bg-[#0055FF]/15 text-[#3377FF] border-[#0055FF]/30 rounded-sm">#{c.rank}</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-200 truncate">{c.name}</div>
                      <div className="text-xs text-zinc-500 truncate">{c.address}</div>
                    </div>
                    <div className="text-xs text-zinc-500">{c.rating ? `★ ${c.rating}` : ""} {c.reviews ? `(${c.reviews})` : ""}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {!!recent.length && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6" data-testid="rank-recent">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Recent checks</div>
          <div className="space-y-1.5">
            {recent.slice(0, 8).map((r) => (
              <div key={r.id} className="text-sm text-zinc-400 flex flex-wrap items-center gap-3">
                <span className="text-zinc-200">{r.keyword}</span>
                <span className="text-zinc-600">·</span>
                <span className="text-zinc-500">{r.location}</span>
                <span className="text-zinc-600 ml-auto">Organic: {r.organic_position ?? "—"} / Local: {r.local_pack_position ?? "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GeoKeywordsTab() {
  const [form, setForm] = useState({ seed: "", city: "", country: "United States" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const run = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/local-seo/keyword-generator", form);
      setResult(data);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={run} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 grid sm:grid-cols-3 gap-3 items-end" data-testid="geo-form">
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Seed keyword</Label>
          <Input value={form.seed} onChange={(e) => setForm({ ...form, seed: e.target.value })} required
                 placeholder="plumber"
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="geo-seed" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">City</Label>
          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required
                 placeholder="Austin"
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="geo-city" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Country</Label>
          <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="geo-country" />
        </div>
        <div className="sm:col-span-3">
          <Button type="submit" disabled={busy} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="geo-run">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MapPin className="w-4 h-4 mr-2" /> Generate keywords</>}
          </Button>
        </div>
      </form>
      {err && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="geo-error">{err}</div>}
      {result && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="geo-result">
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Geo variants ({result.geo_variants.length})</div>
            <div className="flex flex-wrap gap-2">
              {result.geo_variants.map((k, i) => (
                <span key={i} className="text-xs bg-zinc-900 border border-zinc-800 rounded-sm px-2.5 py-1.5 text-zinc-300">{k}</span>
              ))}
            </div>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Related from Google ({result.related_searches.length})</div>
            {result.related_searches.length === 0 ? (
              <p className="text-sm text-zinc-500">No related searches returned.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {result.related_searches.map((k, i) => (
                  <span key={i} className="text-xs bg-[#0055FF]/10 border border-[#0055FF]/30 rounded-sm px-2.5 py-1.5 text-[#3377FF]">{k}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
